import { useEffect, useMemo, useState } from 'react';
import { queryClient } from 'index';
import { useMutation } from '@tanstack/react-query';

import { Modal, Form, notification, Spin, Button, Tag, Flex } from 'antd';
import { ReactComponent as IconClose } from 'assets/images/close.svg';

import QUERY_KEYS from 'services/api/queryKeys';
import useModal from 'stores/useModal';
import API from 'services/api';

import { useGetCountries } from 'hooks/useGetCountries';
import {
  DiscountType,
  TravelAgencyParams,
  CreateAgencyCmsAccountParams
} from 'services/api/type/travel-agency.type';
import { useGetSources } from 'hooks/useGetSources';
import { useGetCompanies } from 'hooks/useGetCompanies';
import { useGetTravelAgency } from 'hooks/useGetTravelAgencies';

import { renderFormRow } from 'components/modal/travel-agency-component/RowForTravelAgencyAction';
import IBButton from 'helper/permission.action';
import { PermissionActionTypes } from 'helper/type/action.type';

function validateCmsAccountFields(fields: {
  email?: string;
  name?: string;
  referral_code?: string;
  referral_discount_type?: DiscountType;
  referral_discount_value?: number;
  external_id?: string;
}): string[] {
  const missing: string[] = [];

  if (!fields.email) missing.push('Email');
  if (!fields.name) missing.push('Name');
  if (!fields.referral_code) missing.push('Referral Code');
  if (!fields.referral_discount_type) missing.push('Referral Discount Type');
  if (fields.referral_discount_value === undefined || fields.referral_discount_value === null)
    missing.push('Referral Discount Value');
  if (!fields.external_id) missing.push('External ID');
  return missing;
}

function buildReferralDiscount(type?: DiscountType, value?: number) {
  return {
    type: type || DiscountType.PERCENT,
    value: value ?? 0
  };
}

const UpdateAndDeleteTravelAgency = ({
  isOpen,
  onCancel,
  travelAgencyId,
  t
}: {
  isOpen: boolean;
  onCancel: () => void;
  travelAgencyId: string;
  t: (key: string) => string;
}) => {
  const { setInfoConfirmModal } = useModal();

  const [form] = Form.useForm();
  const { data: countries } = useGetCountries();
  const { data: sources } = useGetSources();
  const { data: companies } = useGetCompanies();
  const { data: travelAgencyData, isLoading } = useGetTravelAgency(Number(travelAgencyId));

  const { mutateAsync: mutateUpdateTravelAgency, isPending: isSaving } = useMutation({
    mutationFn: (values: TravelAgencyParams) =>
      API.ta.updateTravelAgency(Number(travelAgencyId), values)
  });

  const { mutateAsync: mutateDeleteTravelAgency } = useMutation({
    mutationFn: () => API.ta.deleteTravelAgency(Number(travelAgencyId))
  });

  const { mutateAsync: mutateCreateCmsAccount } = useMutation({
    mutationFn: (data: CreateAgencyCmsAccountParams) => API.ta.createAgencyCmsAccount(data)
  });

  const [isCreatingCms, setIsCreatingCms] = useState(false);

  const isDisabled = travelAgencyData?.is_system ?? false;

  const invalidateTravelAgencies = (id?: number) =>
    queryClient.invalidateQueries({
      queryKey: id ? [QUERY_KEYS.GET_TRAVEL_AGENCIES, id] : [QUERY_KEYS.GET_TRAVEL_AGENCIES]
    });

  const otaSource = useMemo(() => sources?.find(s => s.name === 'OTA'), [sources]);
  const source_id = Form.useWatch('source_id', form);

  const isOtaSource = otaSource?.id === source_id;

  const saveForm = async (values?: TravelAgencyParams): Promise<TravelAgencyParams | null> => {
    if (isDisabled) return null;

    try {
      const validated = values ?? (await form.validateFields());
      const filteredValues = { ...validated };

      delete filteredValues.is_system;

      await mutateUpdateTravelAgency(filteredValues);
      await invalidateTravelAgencies();

      return filteredValues;
    } catch (err: any) {
      notification.error({ message: err.error || t('common.error') });
      return null;
    }
  };

  const handleCreateCmsAccount = async () => {
    if (isCreatingCms) return;

    setIsCreatingCms(true);

    const savedValues = await saveForm();
    if (!savedValues) {
      setIsCreatingCms(false);
      return;
    }

    const {
      email,
      name,
      referral_code,
      referral_discount_type,
      referral_discount_value,
      external_id
    } = { ...travelAgencyData, ...savedValues };

    const medium = ['Kin Website', 'Kin App'].includes(external_id || '')
      ? external_id
      : crypto.randomUUID();

    const missingFields = validateCmsAccountFields({
      email,
      name,
      referral_code,
      referral_discount_type,
      referral_discount_value,
      external_id: medium
    });

    if (missingFields.length > 0) {
      notification.error({
        message: t('managePage.travelAgencyPage.taActions.missingRequiredFields').replace(
          '{{fields}}',
          missingFields.join(', ')
        )
      });
      setIsCreatingCms(false);
      return;
    }

    try {
      const newAccount = await mutateCreateCmsAccount({
        email: email as string,
        fullname: name,
        referralCode: referral_code,
        referralDiscount: buildReferralDiscount(referral_discount_type, referral_discount_value),
        medium
      });

      await mutateUpdateTravelAgency({
        ...savedValues,
        external_id: medium,
        cms_user_id: newAccount.documentId
      });

      await invalidateTravelAgencies(Number(travelAgencyId));
      notification.success({ message: t('common.message.saveSuccessfully') });
    } catch (err: any) {
      notification.error({
        message: err.error || t('managePage.travelAgencyPage.taActions.createCmsAccountFailed')
      });
    } finally {
      setIsCreatingCms(false);
    }
  };

  const onUpdateAgencyCmsAccount = async (filteredValues: TravelAgencyParams) => {
    const { email, referral_code, referral_discount_type, referral_discount_value } =
      filteredValues;
    const { cms_user_id, name, external_id } = travelAgencyData || {};

    if (!cms_user_id || isOtaSource) return;

    const missingFields = validateCmsAccountFields({
      email,
      name,
      referral_code,
      referral_discount_type,
      referral_discount_value,
      external_id
    });

    if (missingFields.length > 0) {
      notification.warning({
        message: t('managePage.travelAgencyPage.taActions.syncCmsSkipped').replace(
          '{{fields}}',
          missingFields.join(', ')
        )
      });
      return;
    }

    try {
      await API.ta.updateAgencyCmsAccount(cms_user_id, {
        referralCode: referral_code,
        referralDiscount: buildReferralDiscount(referral_discount_type, referral_discount_value)
      });
    } catch (err: any) {
      notification.error({
        message: t('managePage.travelAgencyPage.taActions.syncCmsFailed')
      });
    }
  };

  const onFinish = async (values: TravelAgencyParams) => {
    const saved = await saveForm(values);
    if (!saved) return;
    notification.success({ message: t('common.message.saveSuccessfully') });
    await onUpdateAgencyCmsAccount(saved);
    onCancel();
  };

  const handleDelete = async () => {
    setInfoConfirmModal(true, {
      title: t('common.confirm.deleteTa'),
      onOk: async () => {
        try {
          await mutateDeleteTravelAgency();
          await invalidateTravelAgencies();

          if (travelAgencyData?.cms_user_id) {
            await API.ta.deleteAgencyCmsAccount(travelAgencyData.cms_user_id);
          }

          notification.success({
            message: t('common.message.deleteSuccessfully')
          });

          onCancel();
        } catch (err: any) {
          notification.error({
            message: err.error || t('common.error')
          });
        } finally {
          setInfoConfirmModal(false);
        }
      }
    });
  };

  useEffect(() => {
    if (travelAgencyData) {
      const truthyValues = Object.fromEntries(
        Object.entries(travelAgencyData).filter(([, v]) => v != null)
      );

      form.setFieldsValue(truthyValues);
    }

    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, travelAgencyData, form]);

  return (
    <Modal
      title={t('managePage.travelAgencyPage.taActions.title')}
      centered
      width={800}
      open={isOpen}
      onCancel={onCancel}
      closeIcon={<IconClose />}
      className="modal-update-or-delete-travel-agency-info"
      footer={
        <Flex justify="space-between" align="center">
          <div>
            {!isOtaSource &&
              (!travelAgencyData?.cms_user_id ? (
                <Button
                  key="createCms"
                  onClick={handleCreateCmsAccount}
                  loading={isSaving || isCreatingCms}
                >
                  {t('managePage.travelAgencyPage.taActions.createCmsAccount')}
                </Button>
              ) : (
                <Tag key="cmsTag" color="success">
                  {t('managePage.travelAgencyPage.taActions.cmsAccountCreated')}
                </Tag>
              ))}
          </div>
          <Flex gap="small">
            <Button
              key="submit"
              type="primary"
              onClick={form.submit}
              loading={isSaving}
              disabled={isDisabled}
            >
              {t('managePage.travelAgencyPage.taActions.saveButton')}
            </Button>
            <IBButton
              permission={PermissionActionTypes.TA_MANAGEMENT_DELETE}
              key="delete"
              danger
              onClick={handleDelete}
              disabled={isDisabled}
            >
              {t('managePage.travelAgencyPage.taActions.deleteButton')}
            </IBButton>
          </Flex>
        </Flex>
      }
    >
      <Spin spinning={isLoading}>
        <Form
          form={form}
          name="travel-agency-info-form"
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ referral_discount_type: DiscountType.PERCENT }}
          autoComplete="off"
          style={{ width: '100%', padding: '16px' }}
        >
          {renderFormRow({ countries, companies, sources, t })}
        </Form>
      </Spin>
    </Modal>
  );
};

export default UpdateAndDeleteTravelAgency;
